<template>
  <el-drawer
    v-model="visibleProxy"
    direction="rtl"
    size="420px"
    class="notice-drawer"
    :with-header="false"
    append-to-body
  >
    <div class="h-full flex flex-col bg-white">
      <div class="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
        <div class="min-w-0">
          <div class="text-base font-bold text-gray-900">消息提醒</div>
          <div class="text-xs text-gray-400 mt-1">{{ unreadCount }} 条未读</div>
        </div>
        <div class="flex items-center gap-2">
          <button
            class="h-9 w-9 rounded-full hover:bg-gray-50 inline-flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors"
            title="刷新"
            @click="loadMessages"
          >
            <el-icon><Refresh /></el-icon>
          </button>
          <button
            class="h-9 w-9 rounded-full hover:bg-gray-50 inline-flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors"
            title="关闭"
            @click="visibleProxy = false"
          >
            <el-icon><Close /></el-icon>
          </button>
        </div>
      </div>

      <div class="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
        <button
          v-for="tab in statusTabs"
          :key="tab.value"
          class="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
          :class="activeStatus === tab.value ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'"
          @click="switchStatus(tab.value)"
        >
          {{ tab.label }}
        </button>
        <button
          class="ml-auto text-sm text-indigo-600 hover:text-indigo-700 disabled:text-gray-300"
          :disabled="unreadCount <= 0 || markingAll"
          @click="handleMarkAllRead"
        >
          全部已读
        </button>
      </div>

      <div class="flex-1 overflow-y-auto bg-gray-50/70">
        <div v-if="loading" class="p-5 space-y-3">
          <el-skeleton v-for="item in 4" :key="item" animated>
            <template #template>
              <div class="bg-white border border-gray-100 rounded-lg p-4">
                <el-skeleton-item variant="h3" class="!w-2/3" />
                <el-skeleton-item variant="text" class="!w-full mt-3" />
                <el-skeleton-item variant="text" class="!w-1/2" />
              </div>
            </template>
          </el-skeleton>
        </div>

        <el-empty
          v-else-if="messages.length === 0"
          description="暂无消息"
          :image-size="86"
          class="mt-16"
        />

        <div v-else class="p-4 space-y-3">
          <article
            v-for="item in messages"
            :key="item.id"
            class="bg-white border rounded-lg p-4 shadow-sm transition-colors"
            :class="isUnread(item) ? 'border-indigo-100' : 'border-gray-100'"
          >
            <div class="flex items-start gap-3">
              <div
                class="mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                :class="levelClass(item.level)"
              >
                <el-icon><component :is="levelIcon(item.level)" /></el-icon>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-start gap-2">
                  <h3 class="text-sm font-bold text-gray-900 leading-5 break-words flex-1">
                    {{ item.title }}
                  </h3>
                  <span
                    v-if="isUnread(item)"
                    class="mt-1 h-2 w-2 rounded-full bg-indigo-500 shrink-0"
                  ></span>
                </div>
                <p class="text-sm text-gray-500 leading-6 mt-1 break-words">{{ item.content }}</p>
                <div class="flex flex-wrap items-center gap-2 mt-3 text-xs text-gray-400">
                  <span>{{ domainLabel(item.domain) }}</span>
                  <span v-if="businessNo(item)">#{{ businessNo(item) }}</span>
                  <span>{{ formatTime(createdAt(item)) }}</span>
                </div>
              </div>
            </div>

            <div class="mt-4 flex items-center justify-end gap-2">
              <el-button
                v-if="item.link_path || item.linkPath"
                size="small"
                type="primary"
                plain
                @click="openBusiness(item)"
              >
                {{ item.action_label || item.actionLabel || '查看' }}
              </el-button>
              <el-button
                v-if="item.status !== 'handled' && item.status !== 'closed'"
                size="small"
                :icon="Check"
                @click="handleMarkHandled(item)"
              >
                已处理
              </el-button>
            </div>
          </article>
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Check, CircleCheckFilled, Close, Refresh, WarningFilled, InfoFilled } from '@element-plus/icons-vue'
import { getNoticeList, markAllNoticesRead, markNoticeHandled, markNoticeRead } from '../api/notice'
import type { NoticeLevel, NoticeMessage, NoticeStatus } from '../types/notice'

const props = defineProps<{
  modelValue: boolean
  unreadCount: number
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'refreshCount'): void
}>()

const router = useRouter()
const loading = ref(false)
const markingAll = ref(false)
const activeStatus = ref<NoticeStatus | 'all'>('all')
const messages = ref<NoticeMessage[]>([])
const pageNum = ref(1)
const pageSize = 20

const statusTabs: Array<{ label: string; value: NoticeStatus | 'all' }> = [
  { label: '全部', value: 'all' },
  { label: '未读', value: 'unread' },
  { label: '已处理', value: 'handled' }
]

const visibleProxy = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
})

const loadMessages = async () => {
  if (!visibleProxy.value) return
  loading.value = true
  try {
    const res = await getNoticeList({
      pageNum: pageNum.value,
      pageSize,
      status: activeStatus.value
    })
    messages.value = res.data?.records || []
  } catch {
    messages.value = []
  } finally {
    loading.value = false
  }
}

const switchStatus = (status: NoticeStatus | 'all') => {
  activeStatus.value = status
  pageNum.value = 1
  loadMessages()
}

const handleMarkAllRead = async () => {
  markingAll.value = true
  try {
    await markAllNoticesRead()
    emit('refreshCount')
    await loadMessages()
  } finally {
    markingAll.value = false
  }
}

const markReadIfNeeded = async (item: NoticeMessage) => {
  if (!isUnread(item)) return
  await markNoticeRead(item.id)
  item.status = 'read'
  emit('refreshCount')
}

const handleMarkHandled = async (item: NoticeMessage) => {
  await markNoticeHandled(item.id)
  item.status = 'handled'
  emit('refreshCount')
  if (activeStatus.value === 'unread') {
    await loadMessages()
  }
}

const openBusiness = async (item: NoticeMessage) => {
  await markReadIfNeeded(item)
  const target = item.link_path || item.linkPath
  if (target) {
    await router.push(target)
    visibleProxy.value = false
  }
}

const isUnread = (item: NoticeMessage) => item.status === 'unread'
const businessNo = (item: NoticeMessage) => item.business_no || item.businessNo || ''
const createdAt = (item: NoticeMessage) => item.created_at || item.createdAt || ''

const domainLabel = (domain?: string) => {
  const map: Record<string, string> = {
    production: '生产',
    approval: '审批',
    quality: '质检',
    system: '系统'
  }
  return domain ? map[domain] || domain : '消息'
}

const levelClass = (level?: NoticeLevel) => {
  const map: Record<string, string> = {
    success: 'bg-emerald-50 text-emerald-600',
    warning: 'bg-amber-50 text-amber-600',
    error: 'bg-rose-50 text-rose-600',
    info: 'bg-indigo-50 text-indigo-600'
  }
  return map[level || 'info'] || map.info
}

const levelIcon = (level?: NoticeLevel) => {
  if (level === 'success') return CircleCheckFilled
  if (level === 'warning' || level === 'error') return WarningFilled
  return InfoFilled
}

const formatTime = (value?: string) => {
  if (!value) return ''
  const date = new Date(value.replace(/-/g, '/'))
  if (Number.isNaN(date.getTime())) return value
  const now = Date.now()
  const diff = now - date.getTime()
  if (diff < 60 * 1000) return '刚刚'
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)} 分钟前`
  if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / 3600000)} 小时前`
  return `${date.getMonth() + 1}-${date.getDate()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

watch(() => props.modelValue, (visible) => {
  if (visible) {
    loadMessages()
  }
})
</script>

<style scoped>
.notice-drawer :deep(.el-drawer__body) {
  padding: 0;
}
</style>
